import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  ICreatePermission,
  IDiscoveryResult,
  IPagerSettings,
  IPermission,
  IPermissionFilter,
  IUpdatePermission,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { IPermissionsResponse } from './models';

@Injectable({ providedIn: 'root' })
export class PermissionsDataService {
  private http = inject(HttpClient);
  private environment = inject(EnvironmentService);
  private baseUrl = `${this.environment.getApiUrl('nomoh')}/permissions`;

  // Estado com Signals (PADRÃO DO PROJETO)
  private _permissions = signal<IPermission[]>([]);
  private _loading = signal<boolean>(false);
  private _erro = signal<string | null>(null);

  // ✅ Paginação separada por contexto (Tab 1 vs Tab 3)
  private _paginacaoDiscovertas = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 20,
    param: '',
    ordenacao: 0,
  });
  private _paginacaoStandalone = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 20,
    param: '',
    ordenacao: 0,
  });

  private _filtros = signal<Omit<IPermissionFilter, 'page' | 'pageSize'>>({
    sistemaId: undefined,
    module: undefined,
    search: undefined,
    isActive: undefined,
  });
  // Contexto da tab ativa (plan, standalone, ou undefined para todas)
  private _contextType = signal<'plan' | 'standalone' | undefined>(undefined);

  // Signals públicos (readonly)
  $permissions = this._permissions.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();
  $filtros = this._filtros.asReadonly();

  // ✅ Paginação dinâmica baseada no contexto
  $paginacao = computed(() => {
    const context = this._contextType();
    return context === 'standalone'
      ? this._paginacaoStandalone()
      : this._paginacaoDiscovertas();
  });

  // Computed signals
  $temProxPagina = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    return offSet + limit < total;
  });

  $temPagAnterior = computed(() => {
    const offSet = this.$paginacao().offSet ?? 0;
    return offSet > 0;
  });

  $paginaAtual = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    return Math.floor(offSet / limit) + 1;
  });

  $totalPaginas = computed(() => {
    const pag = this.$paginacao();
    const total = pag.total ?? 0;
    const limit = pag.limit ?? 20;
    return Math.ceil(total / limit);
  });

  $infoTotal = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    const inicio = offSet + 1;
    const fim = Math.min(offSet + limit, total);
    return `${inicio}-${fim} de ${total}`;
  });

  // Métodos de estado
  setPermissions(response: IPermissionsResponse) {
    if (response.success) {
      this._permissions.set(response.data);

      // ✅ Salvar paginação no estado correto baseado no contexto
      const context = this._contextType();
      if (context === 'standalone') {
        this._paginacaoStandalone.set(response.pagerSettings);
      } else {
        this._paginacaoDiscovertas.set(response.pagerSettings);
      }
    } else {
      this.setErro(response.errorMessage);
    }
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  setFiltros(filtros: Partial<Omit<IPermissionFilter, 'page' | 'pageSize'>>) {
    this._filtros.update((current) => ({ ...current, ...filtros }));
  }

  // ========================================
  // CONTEXTO (Tab Ativa)
  // ========================================

  /**
   * Define o contexto da tab ativa (plan, standalone, ou undefined)
   * Usado para preservar o filtro type ao navegar/pesquisar
   */
  setContextType(type: 'plan' | 'standalone' | undefined): void {
    this._contextType.set(type);
  }

  // ========================================
  // TAB 1: PERMISSÕES DESCOBERTAS
  // ========================================

  /**
   * Operação: Listar permissões descobertas
   * API: POST /permissions/findAll
   */
  async carregarPermissions(opcoes?: {
    offSet?: number;
    limit?: number;
    type?: 'plan' | 'standalone';
    sistemaId?: string;
  }): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pag = this.$paginacao();

      // ✅ Usar contextType se não foi especificado type nas opções
      const typeFilter = opcoes?.type ?? this._contextType();

      const filtroDto: IPermissionFilter & IPagerSettings = {
        ...this.$filtros(),
        offSet: opcoes?.offSet ?? pag.offSet ?? 0,
        limit: opcoes?.limit ?? pag.limit ?? 20,
        param: pag.param ?? '',
        ordenacao: pag.ordenacao ?? 0,
        type: typeFilter, // ✅ Usar contextType como fallback
        sistemaId: opcoes?.sistemaId, // ✅ Adicionar filtro sistemaId
      };

      const response = await firstValueFrom(
        this.http.post<IPermissionsResponse>(
          `${this.baseUrl}/findAll`,
          filtroDto
        )
      );

      this.setPermissions(response);
    } catch (error) {
      this.setErro('Erro ao carregar permissões');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Forçar discovery de permissões
   * API: POST /permissions/discover
   */
  async forceRescan(): Promise<IDiscoveryResult | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const result = await firstValueFrom(
        this.http.post<IDiscoveryResult>(`${this.baseUrl}/discover`, {})
      );

      // Recarregar lista após discovery
      await this.carregarPermissions();
      return result;
    } catch (error) {
      this.setErro('Erro ao descobrir permissões');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Atualizar permissão descoberta
   * API: PATCH /permissions/:id
   */
  async atualizarPermission(permission: IUpdatePermission): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const sucesso = await firstValueFrom(
        this.http.put<boolean>(`${this.baseUrl}/${permission.id}`, permission)
      );

      if (sucesso) {
        // Recarregar lista após atualizar
        await this.carregarPermissions();
      }

      return sucesso;
    } catch (error) {
      this.setErro('Erro ao atualizar permissão');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // TAB 2: PERMISSÕES DE USUÁRIOS
  // ========================================

  /**
   * Operação: Buscar todas as permissões de um usuário
   * API: GET /pessoa/admin/extra-permissions/:userId
   */
  async buscarPermissoesUsuario(userId: string): Promise<{
    fromPlan: IPermission[];
    extra: IPermission[];
  } | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      // ✅ Endpoint correto: /pessoa/admin/extra-permissions/:userId
      const url = `${this.environment.getApiUrl(
        'nomoh'
      )}/pessoa/admin/extra-permissions/${userId}`;

      const result = await firstValueFrom(
        this.http.get<{
          planPermissions: IPermission[];
          extraPermissions: IPermission[];
        }>(url)
      );

      // ✅ Mapear nomes do backend (planPermissions/extraPermissions) para frontend (fromPlan/extra)
      return {
        fromPlan: result.planPermissions || [],
        extra: result.extraPermissions || [],
      };
    } catch (error) {
      this.setErro('Erro ao buscar permissões do usuário');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Adicionar permissões extras a um usuário
   * API: POST /pessoa/admin/:userId/extra-permissions
   */
  async adicionarPermissoesExtras(
    userId: string,
    availabilityIds: string[]
  ): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      await firstValueFrom(
        this.http.post(
          `${this.environment.getApiUrl(
            'nomoh'
          )}/pessoa/admin/extra-permissions/add`,
          { userId, availabilityIds }
        )
      );
      return true;
    } catch (error) {
      this.setErro('Erro ao adicionar permissões extras');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Remover permissões extras de um usuário
   * API: DELETE /pessoa/admin/extra-permissions/remove
   */
  async removerPermissoesExtras(
    userId: string,
    availabilityIds: string[]
  ): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      await firstValueFrom(
        this.http.delete(
          `${this.environment.getApiUrl(
            'nomoh'
          )}/pessoa/admin/extra-permissions/remove`,
          { body: { userId, availabilityIds } }
        )
      );
      return true;
    } catch (error) {
      this.setErro('Erro ao remover permissões extras');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // TAB 3: PERMISSÕES STANDALONE
  // ========================================

  /**
   * Operação: Listar permissões standalone
   * API: POST /permissions/findAll com type: 'standalone'
   */
  async carregarPermissionsStandalone(opcoes?: {
    offSet?: number;
    limit?: number;
  }): Promise<void> {
    // ✅ Setar contextType para 'standalone' ao entrar na tab
    this._contextType.set('standalone');

    // ✅ Reutiliza carregarPermissions com filtro type: 'standalone'
    return this.carregarPermissions({
      ...opcoes,
      type: 'standalone',
    });
  }

  /**
   * Operação: Criar permissão standalone
   * API: POST /permissions
   */
  async criarPermissionStandalone(
    permission: ICreatePermission
  ): Promise<string | null> {
    this.setLoading(true);
    this.setErro(null);

    try {
      // ✅ Usar endpoint padrão POST /permissions
      const response = await firstValueFrom(
        this.http.post<{ id: string; key: string; message: string }>(
          this.baseUrl,
          permission
        )
      );

      // Recarregar lista após criar
      await this.carregarPermissionsStandalone();
      return response.id;
    } catch (error) {
      this.setErro('Erro ao criar permissão standalone');
      console.error(error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Atualizar permissão standalone
   * API: PUT /permissions/:id
   */
  async atualizarPermissionStandalone(
    permission: IUpdatePermission
  ): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      // ✅ Usar endpoint padrão PUT /permissions/:id
      const response = await firstValueFrom(
        this.http.put<{ id: string; key: string; message: string }>(
          `${this.baseUrl}/${permission.id}`,
          permission
        )
      );

      // Recarregar lista após atualizar
      await this.carregarPermissionsStandalone();
      return !!response.id;
    } catch (error) {
      this.setErro('Erro ao atualizar permissão standalone');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Operação: Excluir permissão standalone
   * API: DELETE /permissions/:id
   */
  async excluirPermissionStandalone(id: string): Promise<boolean> {
    this.setLoading(true);
    this.setErro(null);

    try {
      // ✅ Usar endpoint padrão DELETE /permissions/:id
      await firstValueFrom(this.http.delete(`${this.baseUrl}/${id}`));

      // Recarregar lista após excluir
      await this.carregarPermissionsStandalone();
      return true;
    } catch (error) {
      this.setErro('Erro ao excluir permissão standalone');
      console.error(error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // ========================================
  // NAVEGAÇÃO DE PAGINAÇÃO
  // ========================================

  async proximaPagina(): Promise<void> {
    if (this.$temProxPagina()) {
      const pag = this.$paginacao();
      const novoPagOffset = (pag.offSet ?? 0) + (pag.limit ?? 20);
      await this.carregarPermissions({ offSet: novoPagOffset });
    }
  }

  async paginaAnterior(): Promise<void> {
    if (this.$temPagAnterior()) {
      const pag = this.$paginacao();
      const novoPagOffset = Math.max(0, (pag.offSet ?? 0) - (pag.limit ?? 20));
      await this.carregarPermissions({ offSet: novoPagOffset });
    }
  }

  async irParaPagina(pagina: number): Promise<void> {
    const limit = this.$paginacao().limit ?? 20;
    const novoOffset = (pagina - 1) * limit;
    // ✅ Usar contextType preservado (standalone, plan, ou undefined)
    await this.carregarPermissions({ offSet: novoOffset });
  }

  async alterarLimit(novoLimit: number): Promise<void> {
    // ✅ Usar contextType preservado (standalone, plan, ou undefined)
    await this.carregarPermissions({ offSet: 0, limit: novoLimit });
  }

  async alterarOrdenacao(campo: string, direcao: 1 | -1): Promise<void> {
    // ✅ Atualizar paginação correta baseada no contexto
    const context = this._contextType();
    if (context === 'standalone') {
      this._paginacaoStandalone.update((current) => ({
        ...current,
        param: campo,
        ordenacao: direcao,
      }));
    } else {
      this._paginacaoDiscovertas.update((current) => ({
        ...current,
        param: campo,
        ordenacao: direcao,
      }));
    }

    // ✅ Recarregar com nova ordenação
    await this.carregarPermissions({ offSet: 0 });
  }

  // Busca com filtros
  async buscar(filtros: Partial<IPermissionFilter>): Promise<void> {
    this.setFiltros(filtros);
    // ✅ Usar contextType preservado (standalone, plan, ou undefined)
    await this.carregarPermissions({ offSet: 0 }); // Voltar para primeira página
  }

  // Limpar filtros
  async limparFiltros(): Promise<void> {
    this.setFiltros({
      sistemaId: undefined,
      module: undefined,
      search: undefined,
      isActive: undefined,
    });
    // ✅ Usar contextType preservado (standalone, plan, ou undefined)
    await this.carregarPermissions({ offSet: 0 });
  }

  // ========================================
  // BUSCAR PERMISSÕES DISPONÍVEIS (PARA PLANOS)
  // ========================================
  async buscarPermissoesDisponiveis(filter: {
    type?: 'plan' | 'standalone';
    sistemaId?: string;
  }): Promise<any[]> {
    try {
      this._loading.set(true);
      this._erro.set(null);

      // Construir query params
      const params: any = {};
      if (filter.type) params.type = filter.type;
      if (filter.sistemaId) params.sistemaId = filter.sistemaId;

      const response = await firstValueFrom(
        this.http.get<any>(`${this.baseUrl}/available`, { params })
      );

      return response?.items || [];
    } catch (error: any) {
      const errorMessage =
        error?.error?.message || 'Erro ao buscar permissões disponíveis';
      this._erro.set(errorMessage);
      console.error('Erro ao buscar permissões disponíveis:', error);
      return [];
    } finally {
      this._loading.set(false);
    }
  }
}
